// ─────────────────────────────────────────────────────────────────────────────
//  notificationStyles.js
//  Centralised makeStyles for NotificationDrawer and all sub-components
// ─────────────────────────────────────────────────────────────────────────────
import { makeStyles } from "@material-ui/core";

export const useNotificationStyles = makeStyles((theme) => ({
  /* ── Bell button & badge ── */
  notificationButton: {
    background: "linear-gradient(145deg, #ced1ddff 0%, #ffffffff 100%)",
    color: "white",
    boxShadow: "0 8px 32px rgba(102,126,234,0.3)",
    marginRight: "12px",
    transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
    "&:hover": {
      background: "linear-gradient(145deg, #764ba2 0%, #667eea 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 40px rgba(102,126,234,0.4)",
    },
  },
  badge: {
    "& .MuiBadge-badge": {
      background: "#082D6B", color: "white", fontSize: "11px", fontWeight: "700",
      minWidth: "20px", height: "20px", animation: "$pulse 2s infinite",
      boxShadow: "0 2px 8px rgba(8,45,107,0.45)", marginTop: "-10px",
    },
  },
  "@keyframes pulse": {
    "0%":   { boxShadow: "0 0 0 0 rgba(8,45,107,0.7)",  transform: "scale(1)"    },
    "70%":  { boxShadow: "0 0 0 10px rgba(8,45,107,0)", transform: "scale(1.05)" },
    "100%": { boxShadow: "0 0 0 0 rgba(8,45,107,0)",    transform: "scale(1)"    },
  },

  /* ── Drawer paper ── */
  drawerPaper: {
    width: 580,
    height: "calc(100vh - 100px)",
    background: "linear-gradient(180deg, #f8f9ff 0%, #e8eeff 100%)",
    borderTopLeftRadius: "24px",
    borderBottomLeftRadius: "24px",
    boxShadow: "-10px 0 50px rgba(0,0,0,0.1)",
    border: "none",
    marginTop: "75px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  /* ── Header ── */
  header: {
    background: "#082D6B", color: "#fff",
    padding: theme.spacing(1.2, 2),
    position: "relative", overflow: "hidden", flexShrink: 0,
    "&::before": {
      content: '""', position: "absolute", top: "-50%", right: "-50%",
      width: "200%", height: "200%",
      background: "radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)",
      animation: "$shimmer 4s ease-in-out infinite",
    },
  },
  "@keyframes shimmer": {
    "0%, 100%": { transform: "rotate(0deg)"   },
    "50%":      { transform: "rotate(180deg)" },
  },
  headerContent: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    position: "relative", zIndex: 1,
  },
  title: { fontWeight: "700", fontSize: "1.15rem", color: "#fff", textShadow: "0 2px 4px rgba(0,0,0,0.15)" },

  /* ── Tab bar ── */
  tabBar: { display: "flex", background: "#fff", borderBottom: "1px solid #e8eeff", flexShrink: 0 },
  tab: {
    flex: 1, padding: "9px 0", fontSize: "0.79rem", fontWeight: 600,
    cursor: "pointer", border: "none", background: "none", color: "#64748b",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    borderBottom: "3px solid transparent",
    transition: "color 0.15s, border-color 0.15s, background 0.15s", outline: "none",
  },
  tabActive: { color: "#082D6B", borderBottom: "3px solid #082D6B", background: "#f0f4ff" },
  liveDot: {
    background: "#082D6B", color: "#fff", borderRadius: 10,
    fontSize: "0.62rem", fontWeight: 700, padding: "1px 5px", lineHeight: 1.5,
  },

  /* ── Filter panel ── */
  filterPanel: {
    background: "#fff", borderBottom: "1px solid #eef2ff",
    padding: theme.spacing(1.5, 2),
    display: "flex", flexDirection: "column", gap: theme.spacing(1.4), flexShrink: 0,
  },
  filterRow:   { display: "flex", gap: theme.spacing(1), alignItems: "center", flexWrap: "wrap" },
  filterLabel: { fontSize: "0.68rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 2 },
  filterGroup: { display: "flex", flexDirection: "column", flex: 1, minWidth: 130 },

  /* ── Scrollable list ── */
  list: {
    padding: theme.spacing(1.5, 1.5, 2), flex: 1, minHeight: 0, overflow: "auto",
    "&::-webkit-scrollbar":       { width: "5px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#c7d2fe", borderRadius: "8px" },
  },

  /* ── Status filter bar (search tab) ── */
  statusBar: {
    padding: "9px 14px", borderBottom: "1px solid #EEF2F7",
    display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
    flexShrink: 0, background: "#fff",
  },
  sFilterBtn: {
    padding: "4px 13px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700,
    border: "1.5px solid #E0E8F0", background: "#fff", color: "#455A64",
    cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all .18s",
    "&:hover": { borderColor: "#082D6B", color: "#082D6B" },
  },
  sFilterBtnActive: { background: "#082D6B", color: "#fff", borderColor: "#082D6B" },
  searchBox: {
    flex: 1, minWidth: 110, border: "1px solid #E0E8F0", borderRadius: 8,
    padding: "5px 10px", fontSize: "0.72rem",
    fontFamily: "'Nunito',sans-serif", outline: "none",
    "&:focus": { borderColor: "#082D6B" },
  },

  /* ── ManagedAlertCard ── */
  acard: {
    background: "#fff", borderRadius: 12, border: "1.5px solid #E0E8F0",
    overflow: "hidden", marginBottom: 10, position: "relative",
    cursor: "pointer", transition: "box-shadow .2s, border-color .2s",
    "&:hover": { boxShadow: "0 4px 16px rgba(13,71,161,.09)", borderColor: "#BBDEFB" },
  },
  acardExpanded:  { borderColor: "#90CAF9", boxShadow: "0 4px 20px rgba(13,71,161,.13)" },
  acardStripe:    { position: "absolute", left: 0, top: 0, bottom: 0, width: 4, borderRadius: "12px 0 0 12px" },
  acardSummary:   { padding: "11px 14px 11px 18px", display: "flex", alignItems: "center", gap: 10 },
  acardIconWrap:  { width: 36, height: 36, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  acardMid:       { flex: 1, minWidth: 0 },
  acardType:      { fontWeight: 800, fontSize: "0.82rem" },
  acardSub:       { fontSize: "0.71rem", fontWeight: 600, color: "#455A64", whiteSpace: "normal", wordBreak: "break-word" },
  acardMeta:      { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 },
  acardChevron:   { fontSize: 12, color: "#78909C", transition: "transform .2s", marginLeft: 4 },
  acardChevronOpen: { transform: "rotate(180deg)" },

  /* ── Status badges ── */
  badgeOpen:     { background: "#FFEBEE", color: "#C62828", border: "1px solid #FFCDD2", borderRadius: 20, padding: "2px 8px", fontSize: "0.62rem", fontWeight: 800 },
  badgeClosed:   { background: "#E8F5E9", color: "#2E7D32", border: "1px solid #A5D6A7", borderRadius: 20, padding: "2px 8px", fontSize: "0.62rem", fontWeight: 800 },
  badgeRejected: { background: "#EDE7F6", color: "#6A1B9A", border: "1px solid #CE93D8", borderRadius: 20, padding: "2px 8px", fontSize: "0.62rem", fontWeight: 800 },

  /* ── AlertDetail panel ── */
  detailPanel:   { borderTop: "1px solid #EEF2F7", background: "#FAFCFF", padding: "13px 15px 13px 17px" },
  infoGrid2:     { display: "grid", gridTemplateColumns: "1fr 1fr",       gap: 7, marginBottom: 10 },
  infoGrid3:     { display: "grid", gridTemplateColumns: "1fr 1fr 1fr",   gap: 7, marginBottom: 10 },
  infoField:     { background: "#fff", borderRadius: 8, padding: "7px 9px", border: "1px solid #E0E8F0" },
  infoLbl:       { fontSize: "0.6rem",  fontWeight: 800, color: "#78909C", textTransform: "uppercase", letterSpacing: ".3px", marginBottom: 2 },
  infoVal:       { fontSize: "0.72rem", fontWeight: 700, color: "#1A1A2E", lineHeight: 1.3 },
  infoValRed:    { fontSize: "0.72rem", fontWeight: 700, color: "#C62828", lineHeight: 1.3 },

  sectionDivider: { fontSize: "0.63rem", fontWeight: 800, color: "#082D6B", letterSpacing: ".5px", textTransform: "uppercase", margin: "10px 0 6px", paddingBottom: 4, borderBottom: "1px dashed #BBDEFB" },

  /* ── Driver strip ── */
  driverStrip:  { background: "#EFF6FF", borderRadius: 10, padding: "10px 14px", border: "1.5px solid #BFDBFE", display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  driverAvatar: { width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#1976D2,#0D47A1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.85rem", flexShrink: 0 },
  driverName:   { fontSize: "0.82rem", fontWeight: 800, color: "#0f172a" },
  driverMeta:   { fontSize: "0.65rem", fontWeight: 600, color: "#64748b", marginTop: 2 },
  callBtn:      { background: "#1565C0", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontFamily: "'Nunito',sans-serif", transition: "background .15s", flexShrink: 0 },

  /* ── Context banners ── */
  contextBanner: { borderRadius: 8, padding: "9px 11px", marginBottom: 10, border: "1.5px solid" },
  contextTitle:  { fontSize: "0.63rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 6 },
  anomalyBanner: { background: "#FFF8F8", borderRadius: 8, padding: "10px 12px", border: "1.5px solid #FFCDD2", marginBottom: 10 },
  tripBanner:    { background: "#F0FFF4", borderRadius: 8, padding: "10px 12px", border: "1.5px solid #A5D6A7", marginBottom: 10 },
  movedBanner:   { background: "#FFF3E0", border: "1.5px solid #FFCC80", borderRadius: 8, padding: "7px 11px", fontSize: "0.72rem", fontWeight: 800, color: "#E65100", marginBottom: 10 },

  /* ── Remarks textarea ── */
  remarksWrap: { marginBottom: 10 },
  remarksLbl:  { fontSize: "0.63rem", fontWeight: 800, color: "#455A64", textTransform: "uppercase", letterSpacing: ".3px", marginBottom: 5 },
  remarksTa:   { width: "100%", border: "1.5px solid #E0E8F0", borderRadius: 8, padding: "8px 10px", fontSize: "0.72rem", fontFamily: "'Nunito',sans-serif", resize: "none", outline: "none", color: "#1A1A2E", boxSizing: "border-box" },

  /* ── Action buttons row ── */
  actionRow: { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" },
  abtn: {
    padding: "6px 12px", borderRadius: 8, fontSize: "0.7rem", fontWeight: 800,
    border: "none", cursor: "pointer", fontFamily: "'Nunito',sans-serif",
    display: "inline-flex", alignItems: "center", gap: 4,
    transition: "opacity .15s, transform .1s",
    "&:hover": { opacity: 0.82, transform: "translateY(-1px)" },
  },
  btnTrack:   { background: "#E3F2FD", color: "#1565C0" },
  btnImmob:   { background: "#FFEBEE", color: "#B71C1C" },
  btnResolve: { background: "#E8F5E9", color: "#1B5E20" },
  btnReject:  { background: "#EDE7F6", color: "#6A1B9A" },

  /* ── Audit trail ── */
  trail:       { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 },
  trailRow:    { display: "flex", alignItems: "flex-start", gap: 8 },
  trailDot:    { width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 4 },
  trailText:   { fontSize: "0.65rem", color: "#455A64", fontWeight: 600, lineHeight: 1.5 },
  resolvedTag: { fontSize: "0.65rem", fontWeight: 700, color: "#2E7D32", display: "inline-flex", alignItems: "center", gap: 3 },
  rejectedTag: { fontSize: "0.65rem", fontWeight: 700, color: "#6A1B9A", display: "inline-flex", alignItems: "center", gap: 3 },
}));

export const useTooltipStyles = makeStyles(() => ({
  customTooltip: {
    maxWidth: 320,
    backgroundColor: "#1e293b !important",
    color: "#ffffff !important",
    fontSize: "0.875rem",
    padding: "16px !important",
    borderRadius: "14px !important",
    lineHeight: "1.6",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2), 0 10px 10px -5px rgba(0,0,0,0.1)",
  },
  tooltipPopper: { pointerEvents: "auto !important" },
}));
